import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Michael's Painting" },
      { name: "description", content: "How Michael's Painting collects, uses, and protects your information when you request an estimate or use our site." },
      { property: "og:title", content: "Privacy Policy — Michael's Painting" },
      { property: "og:description", content: "How Michael's Painting collects, uses, and protects your information." },
      { name: "robots", content: "index,follow" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const updated = "July 2026";
  return (
    <SiteLayout>
      <section className="bg-[#030303] text-white py-24 sm:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-amber-200 text-xs font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">Privacy Policy</h1>
          <p className="text-sm text-zinc-500 mb-12">Last updated: {updated}</p>

          <div className="prose prose-invert max-w-none space-y-8 text-zinc-300 leading-relaxed">
            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Overview</h2>
              <p>Michael's Painting ("we", "us") operates this website and provides residential and commercial painting services in Wisconsin. This policy explains what information we collect from visitors and customers, how we use it, and the choices you have.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Information We Collect</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong className="text-white">Contact details</strong> you submit through estimate requests, contact forms, or our AI Studio (name, email, phone, address).</li>
                <li><strong className="text-white">Project details</strong> you share to receive an estimate or proposal (room sizes, surfaces, photos, color selections).</li>
                <li><strong className="text-white">Account information</strong> if you sign in to the customer portal (email, encrypted password).</li>
                <li><strong className="text-white">Usage data</strong> such as pages visited, referrer, device type, and anonymized session activity used to improve the site.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">How We Use It</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>To respond to estimate requests and provide proposals, invoices, and project updates.</li>
                <li>To operate the customer portal, including scheduling, messaging, and payment records.</li>
                <li>To improve our website and services through anonymized analytics.</li>
                <li>To send transactional email related to your project (confirmations, receipts, reminders).</li>
              </ul>
              <p className="mt-3">We do not sell your personal information. We do not send marketing email unless you opt in.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Sharing</h2>
              <p>We share information only with service providers who help us operate the business (hosting, email delivery, payment processing) under contract, or when required by law.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Data Retention</h2>
              <p>We retain estimate, proposal, and project records for as long as needed to serve you and meet legal and tax obligations. You can request deletion of your account and associated personal data by contacting us.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Your Choices</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Request a copy of the personal information we hold about you.</li>
                <li>Correct inaccurate information.</li>
                <li>Request deletion (subject to legal record-keeping requirements).</li>
                <li>Opt out of non-essential email communications.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Cookies</h2>
              <p>We use essential cookies to keep you signed in and remember your preferences, and privacy-friendly analytics to understand aggregate site usage. You can control cookies through your browser settings.</p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-white mb-3">Contact</h2>
              <p>
                Questions about this policy? Reach us at{" "}
                <a href="mailto:michaelspainting0@gmail.com" className="text-amber-300 hover:text-amber-200">michaelspainting0@gmail.com</a>{" "}
                or (608) 886-4598.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}